import React, { useEffect, useState } from 'react';

type Format = 'bold' | 'italic' | 'code' | 'link';

const MARKERS: Record<Exclude<Format, 'link'>, string> = { bold: '**', italic: '*', code: '`' };

/** Il campo scrivibile con testo formattabile che contiene la selezione, se c'è. */
function richField(selection: Selection | null): HTMLElement | null {
    const node = selection?.anchorNode;
    const element = node instanceof HTMLElement ? node : node?.parentElement;
    const field = element?.closest<HTMLElement>('.etx-ed[data-rich]');
    return field && field === document.activeElement ? field : null;
}

/** Aggiunge o toglie i segni del formato attorno al testo selezionato in un campo formattabile. */
function applyFormat(format: Format) {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !richField(selection)) return;
    const text = selection.toString();
    if (format === 'link') {
        document.execCommand('insertText', false, `[${text}](https://)`);
        // Seleziona "https://" così l'indirizzo si scrive subito.
        selection.modify('move', 'backward', 'character');
        for (let i = 0; i < 'https://'.length; i++) selection.modify('extend', 'backward', 'character');
        return;
    }
    const marker = MARKERS[format];
    const wrapped = text.length > marker.length * 2 && text.startsWith(marker) && text.endsWith(marker)
        && !(format === 'italic' && text.startsWith('**'));
    document.execCommand('insertText', false, wrapped ? text.slice(marker.length, -marker.length) : `${marker}${text}${marker}`);
}

/**
 * Barra di formattazione sopra il testo selezionato nell'anteprima: grassetto, corsivo,
 * codice, link. Il testo dei blocchi è markdown semplice, quindi la barra aggiunge o
 * toglie i segni (** * ` [](…)) attorno alla selezione; il campo resta in modifica.
 */
const FormatToolbar: React.FC = () => {
    const [box, setBox] = useState<{ top: number; left: number } | null>(null);

    useEffect(() => {
        const update = () => {
            const selection = window.getSelection();
            if (!selection || selection.isCollapsed || !richField(selection)) { setBox(null); return; }
            const rect = selection.getRangeAt(0).getBoundingClientRect();
            if (!rect.width && !rect.height) { setBox(null); return; }
            setBox({ top: Math.max(8, rect.top - 44), left: Math.max(8, rect.left + rect.width / 2) });
        };
        document.addEventListener('selectionchange', update);
        window.addEventListener('scroll', update, true);
        return () => {
            document.removeEventListener('selectionchange', update);
            window.removeEventListener('scroll', update, true);
        };
    }, []);

    if (!box) return null;

    const button = (format: Format, label: string, content: React.ReactNode, shortcut?: string) => (
        <button type="button" aria-label={label} title={shortcut ? `${label} (${shortcut})` : label}
            // Il clic non deve togliere il focus al campo, altrimenti la selezione si perde.
            onMouseDown={e => { e.preventDefault(); applyFormat(format); }}>
            {content}
        </button>
    );

    return (
        <div className="tcs-format" role="toolbar" aria-label="Formattazione" style={{ top: box.top, left: box.left }}>
            {button('bold', 'Grassetto', <strong>B</strong>, 'Ctrl+B')}
            {button('italic', 'Corsivo', <em>I</em>, 'Ctrl+I')}
            {button('code', 'Codice', <i className="bi bi-code" aria-hidden="true" />)}
            {button('link', 'Link', <i className="bi bi-link-45deg" aria-hidden="true" />, 'Ctrl+K')}
        </div>
    );
};

/** Ctrl+B, Ctrl+I, Ctrl+K nei campi formattabili; restituisce true se ha gestito il tasto. */
export function handleFormatShortcut(e: KeyboardEvent): boolean {
    if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey) return false;
    if (!richField(window.getSelection())) return false;
    const key = e.key.toLowerCase();
    const format: Format | null = key === 'b' ? 'bold' : key === 'i' ? 'italic' : key === 'k' ? 'link' : null;
    if (!format) return false;
    e.preventDefault();
    applyFormat(format);
    return true;
}

export default FormatToolbar;
